import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { voice } from '../lib/voice';
import { useStore } from '../store';
import { Avatar } from './Avatar';
import { CloseIcon, PhoneIcon, PhoneOffIcon, SendIcon } from './icons';

/** Uma conversa entre duas pessoas: histórico, envio e a ligação privada. */
export function ConversaPrivada() {
  const activeDmId = useStore((s) => s.activeDmId);
  const conversas = useStore((s) => s.conversas);
  const me = useStore((s) => s.me);
  const mensagens = useStore((s) => (activeDmId ? (s.dmMessages[activeDmId] ?? VAZIO) : VAZIO));
  const setDmMessages = useStore((s) => s.setDmMessages);
  const sessaoVoz = useStore((s) => s.voice);
  const toast = useStore((s) => s.toast);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [carregado, setCarregado] = useState(false);
  const fimRef = useRef<HTMLDivElement>(null);

  const conversa = conversas.find((c) => c.id === activeDmId);

  useEffect(() => {
    if (!activeDmId) return;
    setCarregado(false);
    void api
      .mensagensPrivadas(activeDmId)
      .then(({ mensagens: lista }) => setDmMessages(activeDmId, lista))
      .catch((erro: Error) => toast(erro.message, 'error'))
      .finally(() => setCarregado(true));
  }, [activeDmId, setDmMessages, toast]);

  // Rola para o fim a cada mensagem nova, como em qualquer chat.
  useLayoutEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'end' });
  }, [mensagens.length]);

  if (!activeDmId || !conversa) return null;

  const naLigacao = sessaoVoz.channelId === activeDmId;

  async function enviar() {
    const conteudo = texto.trim();
    if (!conteudo || enviando) return;
    setEnviando(true);
    try {
      await api.enviarPrivada(activeDmId!, conteudo);
      setTexto('');
    } catch (erro) {
      toast(erro instanceof Error ? erro.message : 'Nao consegui enviar.', 'error');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="main chat">
      <div className="main-header">
        <Avatar name={conversa.user.nickname} color={conversa.user.accentColor} size={26} />
        <div className="title">{conversa.user.nickname}</div>
        <div className="spacer" />
        {/* Fecha a conversa e devolve a area central ao canal da guild. */}
        <button
          className="icon-btn"
          title="Fechar conversa"
          aria-label="Fechar conversa"
          onClick={() => useStore.getState().selectDm(null)}
        >
          <CloseIcon />
        </button>
        {naLigacao ? (
          <button
            className="icon-btn danger-hover"
            title="Encerrar a ligacao"
            onClick={() => void voice.leave(true)}
          >
            <PhoneOffIcon />
          </button>
        ) : (
          <button
            className="icon-btn"
            title={`Ligar para ${conversa.user.nickname}`}
            onClick={() => void voice.ligarNoPrivado(activeDmId!)}
          >
            <PhoneIcon />
          </button>
        )}
      </div>

      <div className="messages">
        {!carregado && <div className="channel-intro">Carregando...</div>}
        {carregado && mensagens.length === 0 && (
          <div className="channel-intro">
            Nenhuma mensagem ainda. O que voces escreverem aqui so voces dois veem.
          </div>
        )}
        {mensagens.map((m) => {
          const meu = m.authorId === me?.id;
          const autor = meu ? me : conversa.user;
          return (
            <div key={m.id} className="dm-mensagem">
              <Avatar
                name={autor?.nickname ?? '?'}
                color={autor?.accentColor ?? '#888'}
                size={30}
              />
              <div>
                <div className="dm-cabecalho">
                  <span className="dm-autor">{autor?.nickname}</span>
                  <span className="dm-hora">
                    {new Date(m.createdAt).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <div className="dm-conteudo">{m.content}</div>
              </div>
            </div>
          );
        })}
        <div ref={fimRef} />
      </div>

      <div className="composer">
        <div className="composer-inner">
          <textarea
            rows={1}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void enviar();
              }
            }}
            placeholder={`Conversar com ${conversa.user.nickname}`}
            maxLength={4000}
          />
          <button
            className="composer-enviar"
            title="Enviar"
            aria-label="Enviar mensagem"
            disabled={enviando || !texto.trim()}
            onClick={() => void enviar()}
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Referencia estavel: devolver [] no seletor remontaria a lista a cada render. */
const VAZIO: never[] = [];
